import { Component } from "react";
import { Loader2 } from "lucide-react";

// Catches runtime render errors. Specifically recovers from ChunkLoadError that
// happens after a redeploy when a stale service-worker serves old chunk hashes.
function isChunkError(error) {
  const msg = (error && (error.message || error.name || "")) + "";
  return (
    msg.includes("ChunkLoadError") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    /dynamically imported module/i.test(msg)
  );
}

async function purgeAndReload() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {
    /* ignore */
  }
  window.location.reload();
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunk: isChunkError(error) };
  }

  componentDidMount() {
    // Render succeeded — re-arm auto-recovery for any future chunk error this session.
    if (!this.state.hasError) sessionStorage.removeItem("airpog_chunk_recover");
  }

  componentDidCatch(error) {
    if (isChunkError(error)) {
      // Recover automatically, but only once per session to avoid reload loops.
      const KEY = "airpog_chunk_recover";
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        this.setState({ recovering: true });
        purgeAndReload();
      }
    }
  }

  render() {
    if (this.state.recovering) {
      return (
        <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center gap-4">
          <img src="/pogzilla.png" alt="POGZILLA" className="w-24 h-24 object-contain animate-bounce drop-shadow-[0_0_24px_rgba(52,211,153,0.5)]" />
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
            <span className="font-semibold text-sm">POGZILLA is fetching the latest…</span>
          </div>
        </div>
      );
    }
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col items-center justify-center gap-4 px-6 text-center"
          data-testid="error-boundary"
        >
          <img src="/pogzilla.png" alt="POGZILLA" className="w-28 h-28 object-contain drop-shadow-[0_0_24px_rgba(52,211,153,0.5)]" />
          <div className="text-2xl font-extrabold tracking-tight">
            POGZILLA tripped on something.
          </div>
          <p className="text-slate-400 text-sm max-w-sm">
            We hit a snag loading the latest version. A quick refresh should fix it.
          </p>
          <button
            onClick={purgeAndReload}
            data-testid="error-boundary-reload-btn"
            className="px-5 py-2.5 rounded-full bg-sky-500 hover:bg-sky-400 text-white font-semibold transition-colors"
          >
            Reload AIR.POG
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
